// Microphone to µ-law, and back.
//
// sphoro.voice speaks G.711 µ-law at 8 kHz in both directions — the format a
// telephone carrier hands it, which is what makes a browser call and a phone
// call indistinguishable to everything downstream. A browser has neither: it
// captures 32-bit floats at whatever rate the sound card runs at. The gap
// between those two is a codec and a resampler, and neither is available from
// the platform, so both are here.
//
// This file exists rather than the two callers each having a copy. The agent
// call sends µ-law because that is the whole conversation; a live call sends it
// because sphoro.voice cannot record or transcribe a conversation it is not in
// the middle of, and asks each browser for a copy of its own microphone. Two
// implementations of G.711 in one package would be one implementation and one
// that is subtly wrong on the day somebody fixes only the first.
//
// Nothing is imported to do it. The SDK runs on someone else's page and has no
// dependencies, and that rule does not bend for audio.

/** The wire's sample rate. Not negotiable — see the note above. */
export const WIRE_RATE = 8000;

/**
 * An audio context, at the wire's rate where the browser will give us one.
 *
 * Asking for 8 kHz outright makes the resampler below dead code on most
 * machines. Some browsers refuse a rate their hardware does not like, so the
 * fallback is an ordinary context and the manual path.
 */
export function createContext(rate = WIRE_RATE) {
  const Ctor = window.AudioContext || window.webkitAudioContext;
  try {
    return new Ctor({ sampleRate: rate });
  } catch {
    return new Ctor();
  }
}

/**
 * Start a context that the browser has parked, if it will let us.
 *
 * Resolves either way. There is nothing useful to do with the rejection: it
 * means this browser wants a user gesture we are not inside, and the call is
 * still worth attempting — a context that stays suspended is a silent call,
 * which is what the caller finds out about from the states it is already being
 * sent.
 */
export async function resumeContext(context) {
  if (context.state !== 'suspended') return;
  try {
    await context.resume();
  } catch { /* no activation to spend */ }
}

/**
 * Drop a signal to the wire rate by averaging each source window.
 *
 * Exported for its own test: the browsers that refuse an 8 kHz context are the
 * ones that need this, and they are exactly the browsers a developer is least
 * likely to be looking at when they change it.
 *
 * Averaging rather than picking every nth sample: plain decimation aliases
 * everything above 4 kHz down into the speech band, and consonants are exactly
 * what lives up there. This is a crude low-pass, but it is the difference
 * between "sixty" and "sixteen" surviving the trip.
 */
export function downsample(input, fromRate, toRate) {
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

/** G.711 µ-law: the bias and clip the standard specifies. */
const MU_BIAS = 0x84;
const MU_CLIP = 32635;

/**
 * Float samples to µ-law bytes, ready to put on the wire.
 *
 * Exported for the codec test rather than for callers: this file is not the
 * package's entry point, and a hand-rolled G.711 that nothing checks is a
 * silent call nobody can debug from the outside.
 */
export function encodeMuLaw(samples) {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    // Clamped before scaling: a float outside [-1, 1] is legal in Web Audio and
    // would wrap round to the opposite sign as an integer.
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    let sample = Math.round(clamped * 32767);

    const sign = sample < 0 ? 0x80 : 0;
    if (sign) sample = -sample;
    if (sample > MU_CLIP) sample = MU_CLIP;
    sample += MU_BIAS;

    let exponent = 7;
    for (let mask = 0x4000; (sample & mask) === 0 && exponent > 0; exponent--, mask >>= 1);
    const mantissa = (sample >> (exponent + 3)) & 0x0F;
    out[i] = ~(sign | (exponent << 4) | mantissa) & 0xFF;
  }
  return out;
}

/** µ-law bytes back to float samples, ready for an AudioBuffer. */
export function decodeMuLaw(bytes) {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const byte = ~bytes[i] & 0xFF;
    const sign = byte & 0x80;
    const exponent = (byte >> 4) & 0x07;
    const mantissa = byte & 0x0F;
    const magnitude = (((mantissa << 3) + MU_BIAS) << exponent) - MU_BIAS;
    out[i] = (sign ? -magnitude : magnitude) / 32768;
  }
  return out;
}

/**
 * How much audio rides in one frame.
 *
 * Measured in samples, and the context above runs at 8 kHz wherever the browser
 * allows it — so this is 64 ms of speech, not the 20-odd it would be at
 * hardware rate. That distinction is the whole reason for the number: at 2048
 * the same code sends a quarter of a second per frame, which is a quarter of a
 * second added to the start of every turn and to every barge-in, and it sounds
 * exactly like an agent that is slow to notice you talking.
 *
 * Not smaller than this. ScriptProcessorNode runs on the main thread, and a
 * block it cannot fill in time is a block it drops — which is a gap in the
 * middle of a word rather than a delay before one. Powers of two only.
 */
export const CAPTURE_BLOCK = 512;

/**
 * Tap a microphone and hand each block over as µ-law bytes.
 *
 * ScriptProcessorNode is deprecated in favour of AudioWorklet, and is used
 * anyway. A worklet's processor has to be loaded from a URL, which on this SDK
 * means a blob: URL — and a blob: worklet is refused outright by the
 * Content-Security-Policy of a great many of the sites this code runs on. A
 * deprecated node that works on a customer's page beats a modern one that is
 * blocked by their headers.
 *
 * Returns a `stop` that lets go of the whole chain. The microphone track is
 * deliberately *not* stopped by it: on a live call the same track is carrying
 * the conversation, and stopping it to end a recording would end the call.
 *
 * @param {AudioContext} context
 * @param {MediaStream} stream
 * @param {(bytes: Uint8Array) => void} onFrame
 * @param {number} [wireRate]
 * @returns {{stop: () => void}}
 */
export function tapMicrophone(context, stream, onFrame, wireRate = WIRE_RATE) {
  const source = context.createMediaStreamSource(stream);
  const capture = context.createScriptProcessor(CAPTURE_BLOCK, 1, 1);

  capture.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    const frames = context.sampleRate === wireRate
      ? input
      : downsample(input, context.sampleRate, wireRate);
    onFrame(encodeMuLaw(frames));
  };

  source.connect(capture);
  // A ScriptProcessorNode does not run unless it is connected to a destination,
  // even when nothing is meant to come out of it. Zero gain is what keeps the
  // speaker from hearing their own voice played back at them.
  const mute = context.createGain();
  mute.gain.value = 0;
  capture.connect(mute);
  mute.connect(context.destination);

  return {
    stop() {
      capture.onaudioprocess = null;
      try { source.disconnect(); } catch { /* already gone */ }
      try { capture.disconnect(); } catch { /* already gone */ }
      try { mute.disconnect(); } catch { /* already gone */ }
    },
  };
}
